import React, {
	useRef,
	useState,
	useCallback,
	useEffect,
} from 'react';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import Avatar from '../general-components/avatar';
import type { GreenroomUser } from '../../stores/types/session';

interface ChatInputProps {
	user: GreenroomUser;
	onSendMessage: (message: string) => void;
	disabled?: boolean;
	inputText: string;
	setInputText: (text: string) => void;
}

function ChatInputComponent({
	user,
	onSendMessage,
	disabled = false,
	inputText,
	setInputText,
}: ChatInputProps) {
	const [composing, setComposing] = useState(false);

	const inputRef = useRef<HTMLSpanElement | null>(null);

	const handleInputChange = useCallback((e: React.FormEvent<HTMLSpanElement>) => {
		const {
			innerText,
			textContent,
			innerHTML,
		} = e.currentTarget;

		// if current target's innerHTML is a single line break tag with no text content,
		// all text and whitespace has been removed, so clear it to show the placeholder
		if (!textContent && innerHTML === '<br>') {
			setInputText('');
			e.currentTarget.innerText = '';
		} else {
			setInputText(innerText);
		}
	}, [setInputText]);

	const submitMessage = useCallback(() => {
		const trimmed = inputText.trim();
		if (!trimmed) return;

		onSendMessage(trimmed);
		setInputText('');
		if (inputRef.current) {
			inputRef.current.innerText = '';
		}
	}, [inputText, onSendMessage, setInputText]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLSpanElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !composing) {
			e.preventDefault();
			submitMessage();
		}
	}, [inputText, composing, submitMessage]);

	useEffect(() => {
		if (
			inputRef.current
			&& inputRef.current.innerText !== inputText
		) {
			inputRef.current.innerText = inputText;
		}
	}, [inputText]);

	return (
		<div className='chat-bottom height-fit-content max-height-half'>
			<form onSubmit={(e) => { e.preventDefault(); submitMessage(); }} className="chat-form">
				<div className="avatar-wrapper">
					<Avatar
						size={'chat'}
						user={user}
						noBorder={true}
					/>
				</div>
				<div className="input-wrapper">
					<div className="input-container">
						<span
							contentEditable={!disabled}
							ref={inputRef}
							className="chat-input"
							onKeyDown={handleKeyDown}
							onInput={handleInputChange}
							onPaste={(e) => {
								if (e.clipboardData?.files?.length) {
									e.preventDefault();
								}
							}}
							onCompositionStart={() => setComposing(true)}
							onCompositionEnd={() => setComposing(false)}
							// @ts-ignore
							placeholder="Start typing your message"
						/>
						<div className="form-actions">
							<button className={classNames('send')} type="submit">
								<Icon
									name={ICONS.SEND}
									size={25}
									color={inputText.trim() ? COLORS.WHITE : COLORS.DEFAULT_GRAY}
								/>
							</button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
}

export default React.memo(ChatInputComponent);
